import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { wardrobeApi, formatApiError } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../contexts/LanguageContext';
import { useToast } from '../hooks/use-toast';
import { Shirt, Sparkles, Trash2, Check, Loader2, ArrowRight, Share2, Link2 } from 'lucide-react';

export default function WardrobePage() {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const { t, tField, lang } = useLang();
  const { toast } = useToast();
  const navigate = useNavigate();

  const [wardrobe, setWardrobe] = useState(null);
  const [outfits, setOutfits] = useState([]);
  const [selected, setSelected] = useState([]);
  const [outfitName, setOutfitName] = useState('');
  const [saving, setSaving] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [suggestions, setSuggestions] = useState(null);
  const [shareMenu, setShareMenu] = useState(null);
  const [shareUrl, setShareUrl] = useState('');

  const handleShare = async (outfit) => {
    try {
      const r = await wardrobeApi.shareOutfit(outfit.outfit_id);
      const url = `${window.location.origin}/outfit/${r.data.share_token}`;
      const title = t({ it: `Il mio outfit biolosophy: ${outfit.name}`, en: `My biolosophy outfit: ${outfit.name}` });
      if (navigator.share) {
        try {
          await navigator.share({ title, url });
          return;
        } catch (e) {
          if (e.name === 'AbortError') return;
        }
      }
      setShareUrl(url);
      setShareMenu(outfit.outfit_id);
    } catch (error) {
      toast({ title: t({ it: 'Errore nella condivisione', en: 'Sharing error' }), variant: 'destructive' });
    }
  };

  const copyShareLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast({ title: t({ it: 'Link copiato!', en: 'Link copied!' }) });
    } catch {
      toast({ title: shareUrl });
    }
    setShareMenu(null);
  };

  useEffect(() => {
    if (!authLoading && !isAuthenticated) navigate('/login');
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    if (!isAuthenticated) return;
    wardrobeApi.get().then((r) => setWardrobe(r.data)).catch(() => setWardrobe({ items: [], total_value: 0, count: 0 }));
    wardrobeApi.getOutfits().then((r) => setOutfits(r.data.outfits)).catch(() => {});
  }, [isAuthenticated]);

  const toggleSelect = (productId) => {
    setSuggestions(null);
    setSelected((prev) =>
      prev.includes(productId) ? prev.filter((id) => id !== productId) : prev.length < 10 ? [...prev, productId] : prev
    );
  };

  const handleSaveOutfit = async () => {
    setSaving(true);
    try {
      await wardrobeApi.saveOutfit(outfitName, selected);
      toast({ title: t({ it: 'Outfit salvato!', en: 'Outfit saved!' }) });
      setOutfitName('');
      setSelected([]);
      const r = await wardrobeApi.getOutfits();
      setOutfits(r.data.outfits);
    } catch (error) {
      toast({ title: t({ it: 'Errore', en: 'Error' }), description: formatApiError(error), variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteOutfit = async (outfitId) => {
    try {
      await wardrobeApi.deleteOutfit(outfitId);
      setOutfits((prev) => prev.filter((o) => o.outfit_id !== outfitId));
    } catch (error) {
      toast({ title: t({ it: 'Errore nell\'eliminazione', en: 'Deletion error' }), variant: 'destructive' });
    }
  };

  const handleSuggest = async () => {
    setSuggesting(true);
    setSuggestions(null);
    try {
      const r = await wardrobeApi.suggest(selected);
      setSuggestions(r.data.suggestions);
    } catch (error) {
      toast({ title: t({ it: 'Stylist non disponibile', en: 'Stylist unavailable' }), description: formatApiError(error), variant: 'destructive' });
    } finally {
      setSuggesting(false);
    }
  };

  if (authLoading || !wardrobe) {
    return (
      <div className="min-h-screen pt-32 px-6 md:px-12 lg:px-24">
        <div className="animate-pulse space-y-6">
          <div className="h-10 bg-[#E5E0D8] w-72" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => <div key={i} className="h-72 bg-[#E5E0D8]" />)}
          </div>
        </div>
      </div>
    );
  }

  const selectedItems = wardrobe.items.filter((it, i, arr) => selected.includes(it.product_id) && arr.findIndex((x) => x.product_id === it.product_id) === i);

  return (
    <div data-testid="wardrobe-page" className="min-h-screen bg-[#FAF9F6] pt-24 pb-32">
      {/* Hero */}
      <section className="px-6 md:px-12 lg:px-24 py-12">
        <p className="overline mb-4">{t({ it: 'Il tuo armadio circolare', en: 'Your circular closet' })}</p>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light tracking-tighter text-[#1C1917] mb-6">
          {t({ it: 'Il Mio Guardaroba', en: 'My Wardrobe' })}
        </h1>
        <div className="flex flex-wrap gap-8">
          <div data-testid="wardrobe-count">
            <p className="text-3xl font-light">{wardrobe.count}</p>
            <p className="text-sm text-[#57534E]">{t({ it: 'capi biolosophy', en: 'biolosophy garments' })}</p>
          </div>
          <div data-testid="wardrobe-total-value">
            <p className="text-3xl font-light text-[#4A5D23]">{wardrobe.total_value.toFixed(2)}€</p>
            <p className="text-sm text-[#57534E]">{t({ it: 'valore Biolosophy attuale del guardaroba', en: 'current Biolosophy Value of your wardrobe' })}</p>
          </div>
        </div>
        <p className="text-sm text-[#78716C] mt-4 max-w-xl">
          {t({
            it: 'Ogni capo che possiedi vale uno sconto che cresce nel tempo: seleziona i capi per comporre un outfit e lascia che lo stylist AI ti suggerisca gli abbinamenti.',
            en: 'Every garment you own is worth a growing discount: select items to compose an outfit and let the AI stylist suggest matching pieces.',
          })}
        </p>
      </section>

      {/* Items grid */}
      <section className="px-6 md:px-12 lg:px-24">
        {wardrobe.items.length === 0 ? (
          <div className="bg-[#F2EFE9] p-16 text-center" data-testid="wardrobe-empty">
            <Shirt className="w-10 h-10 mx-auto mb-4 text-[#A8A29E]" />
            <p className="text-[#57534E] mb-6">{t({ it: 'Il tuo guardaroba è ancora vuoto: i capi che acquisti appariranno qui.', en: 'Your wardrobe is still empty: garments you buy will appear here.' })}</p>
            <Link to="/shop" className="btn-primary inline-flex items-center gap-2" data-testid="wardrobe-shop-link">
              {t({ it: 'Scopri la collezione', en: 'Discover the collection' })} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        ) : (
          <div className="flex gap-5 overflow-x-auto pb-6 snap-x" data-testid="wardrobe-items">
            {wardrobe.items.map((item, idx) => {
              const isSel = selected.includes(item.product_id);
              return (
                <button
                  key={item.key}
                  onClick={() => toggleSelect(item.product_id)}
                  data-testid={`wardrobe-item-${idx}`}
                  className={`relative flex-shrink-0 w-56 text-left snap-start group transition-all ${isSel ? 'ring-2 ring-[#4A5D23] ring-offset-4 ring-offset-[#FAF9F6]' : ''}`}
                >
                  {isSel && (
                    <span className="absolute top-3 right-3 z-10 w-7 h-7 bg-[#4A5D23] text-white flex items-center justify-center rounded-full">
                      <Check className="w-4 h-4" />
                    </span>
                  )}
                  <div className="aspect-[3/4] overflow-hidden bg-[#E5E0D8]">
                    {item.image && <img src={item.image} alt={tField(item, 'name')} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}
                  </div>
                  <div className="pt-3">
                    <p className="font-medium text-sm truncate">{tField(item, 'name')}</p>
                    <p className="text-xs text-[#78716C] mt-0.5">
                      {item.size && `${t({ it: 'Taglia', en: 'Size' })} ${item.size} · `}
                      {new Date(item.purchased_at).toLocaleDateString(lang === 'en' ? 'en-GB' : 'it-IT', { month: 'short', year: 'numeric' })}
                    </p>
                    <div className="mt-2 inline-flex items-center gap-1.5 bg-[#4A5D23]/10 text-[#4A5D23] px-2 py-1 text-xs font-medium" data-testid={`wardrobe-value-${idx}`}>
                      {t({ it: 'Vale', en: 'Worth' })} {item.value_percentage}% · ≈{item.value_eur.toFixed(0)}€
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </section>

      {/* Suggestions */}
      {suggestions && (
        <section className="px-6 md:px-12 lg:px-24 mt-12" data-testid="stylist-suggestions">
          <h2 className="text-2xl font-light mb-2 flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-[#B8860B]" />
            {t({ it: 'Lo stylist suggerisce', en: 'The stylist suggests' })}
          </h2>
          {suggestions.length === 0 ? (
            <p className="text-sm text-[#57534E]">{t({ it: 'Nessun abbinamento disponibile al momento.', en: 'No matches available right now.' })}</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-6">
              {suggestions.map((s, idx) => (
                <Link key={s.product_id} to={`/product/${s.product_id}`} className="group bg-white border border-[#E5E0D8] hover:border-[#1C1917] transition-colors" data-testid={`stylist-suggestion-${idx}`}>
                  <div className="aspect-[3/4] overflow-hidden bg-[#E5E0D8]">
                    {s.image && <img src={s.image} alt={tField(s, 'name')} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}
                  </div>
                  <div className="p-4">
                    <p className="font-medium text-sm">{tField(s, 'name')}</p>
                    <p className="text-xs text-[#57534E] italic mt-1">"{s.reason}"</p>
                    <p className="text-sm text-[#4A5D23] font-medium mt-2">{s.price?.toFixed(2)}€ <ArrowRight className="w-3.5 h-3.5 inline" /></p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>
      )}

      {/* Saved outfits */}
      {outfits.length > 0 && (
        <section className="px-6 md:px-12 lg:px-24 mt-16" data-testid="saved-outfits">
          <h2 className="text-2xl font-light mb-6">{t({ it: 'I Miei Outfit', en: 'My Outfits' })}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {outfits.map((o, idx) => (
              <div key={o.outfit_id} className="bg-white border border-[#E5E0D8] p-4" data-testid={`outfit-${idx}`}>
                <div className="flex items-center justify-between mb-3">
                  <p className="font-medium">{o.name}</p>
                  <div className="flex items-center gap-2">
                    <button onClick={() => handleShare(o)} data-testid={`share-outfit-${idx}`} className="text-[#A8A29E] hover:text-[#4A5D23] transition-colors" aria-label="Condividi outfit">
                      <Share2 className="w-4 h-4" />
                    </button>
                    <button onClick={() => handleDeleteOutfit(o.outfit_id)} data-testid={`delete-outfit-${idx}`} className="text-[#A8A29E] hover:text-[#C05746] transition-colors" aria-label="Elimina outfit">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                {shareMenu === o.outfit_id && (
                  <div className="mb-3 p-3 bg-[#F2EFE9] space-y-2" data-testid="share-menu">
                    <div className="flex flex-wrap gap-2">
                      <a href={`https://wa.me/?text=${encodeURIComponent(`${t({ it: 'Guarda il mio outfit biolosophy', en: 'Check out my biolosophy outfit' })}: ${shareUrl}`)}`} target="_blank" rel="noopener noreferrer" className="px-3 py-1.5 text-xs bg-[#25D366] text-white" data-testid="share-whatsapp">WhatsApp</a>
                      <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noopener noreferrer" className="px-3 py-1.5 text-xs bg-[#1877F2] text-white" data-testid="share-facebook">Facebook</a>
                      <a href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(t({ it: 'Il mio outfit biolosophy', en: 'My biolosophy outfit' }))}`} target="_blank" rel="noopener noreferrer" className="px-3 py-1.5 text-xs bg-black text-white" data-testid="share-x">X</a>
                      <button onClick={copyShareLink} className="px-3 py-1.5 text-xs bg-[#1C1917] text-white flex items-center gap-1" data-testid="share-copy-link">
                        <Link2 className="w-3 h-3" /> {t({ it: 'Copia link', en: 'Copy link' })}
                      </button>
                    </div>
                    <p className="text-[11px] text-[#78716C] break-all">{shareUrl}</p>
                  </div>
                )}
                <div className="flex gap-2">
                  {o.items.map((it) => (
                    <div key={it.product_id} className="w-14 h-20 bg-[#E5E0D8] overflow-hidden flex-shrink-0">
                      {it.image && <img src={it.image} alt={tField(it, 'name')} className="w-full h-full object-cover" />}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Sticky outfit builder bar */}
      {selected.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-50 bg-[#1C1917] text-white p-4" data-testid="outfit-builder-bar">
          <div className="max-w-5xl mx-auto flex flex-wrap items-center gap-4">
            <div className="flex -space-x-2">
              {selectedItems.slice(0, 5).map((it) => (
                <div key={it.product_id} className="w-10 h-10 rounded-full overflow-hidden border-2 border-[#1C1917] bg-[#E5E0D8]">
                  {it.image && <img src={it.image} alt="" className="w-full h-full object-cover" />}
                </div>
              ))}
            </div>
            <span className="text-sm text-white/70">
              {selected.length}{' '}
              {selected.length === 1
                ? t({ it: 'capo selezionato', en: 'item selected' })
                : t({ it: 'capi selezionati', en: 'items selected' })}
            </span>
            <div className="flex-1" />
            <input
              type="text"
              value={outfitName}
              onChange={(e) => setOutfitName(e.target.value)}
              placeholder={t({ it: 'Nome outfit (es. Weekend in campagna)', en: 'Outfit name (e.g. Country weekend)' })}
              data-testid="outfit-name-input"
              className="bg-white/10 border border-white/20 px-3 py-2 text-sm placeholder:text-white/40 focus:outline-none focus:border-white w-64"
            />
            <button
              onClick={handleSaveOutfit}
              disabled={!outfitName.trim() || saving}
              data-testid="save-outfit-btn"
              className="px-4 py-2 text-sm bg-white text-[#1C1917] hover:bg-white/80 transition-colors disabled:opacity-40"
            >
              {saving ? t({ it: 'Salvo...', en: 'Saving...' }) : t({ it: 'Salva outfit', en: 'Save outfit' })}
            </button>
            <button
              onClick={handleSuggest}
              disabled={suggesting}
              data-testid="stylist-suggest-btn"
              className="flex items-center gap-2 px-4 py-2 text-sm bg-[#4A5D23] hover:bg-[#3B4A1C] transition-colors disabled:opacity-40"
            >
              {suggesting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {suggesting ? t({ it: 'Lo stylist pensa...', en: 'Stylist thinking...' }) : t({ it: 'Abbina con AI', en: 'Match with AI' })}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
